import React, { useEffect, useState, useCallback } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { ShieldAlert, Activity as ActivityIcon, Ban, RotateCcw } from "lucide-react";

export default function Activity() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/v1/admin/activity").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const unsuspend = async (uid) => {
    try { await api.post("/v1/admin/unsuspend", { user_id: uid }); toast.success("Account reinstated"); load(); }
    catch { toast.error("Failed"); }
  };

  const fmtTs = (ts) => (ts ? `${formatDate(ts)} ${String(ts).slice(11, 19)}` : "—");

  return (
    <div>
      <PageHeader
        eyebrow="Admin · security"
        title="Suspicious Activity"
        subtitle="Per-user read volume, auto-suspended accounts and the latest access events. Rate limits and quotas block bulk scraping automatically."
      />

      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
          <ActivityIcon className="w-5 h-5 text-blue-400 mb-3" />
          <div className="font-mono text-2xl font-bold text-slate-100">{(data?.total_today ?? 0).toLocaleString()}</div>
          <div className="text-sm text-slate-300">Reads today</div>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
          <ShieldAlert className="w-5 h-5 text-blue-400 mb-3" />
          <div className="font-mono text-sm font-bold text-slate-100">{data?.limits?.per_min}/min · {data?.limits?.per_day}/day</div>
          <div className="text-sm text-slate-300">Per-user limits</div>
          <div className="text-[11px] text-slate-500">spike guard: {data?.limits?.spike_10s}/10s → auto-suspend</div>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
          <Ban className="w-5 h-5 text-rose-600 mb-3" />
          <div className="font-mono text-2xl font-bold text-rose-700">{data?.suspended?.length ?? 0}</div>
          <div className="text-sm text-rose-700">Suspended accounts</div>
        </div>
      </div>

      {data?.suspended?.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-200 mb-3">Suspended accounts</h3>
          <div className="space-y-2">
            {data.suspended.map((s) => (
              <div key={s.user_id} data-testid={`suspended-${s.user_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
                <div>
                  <div className="font-medium text-slate-100">{s.email}</div>
                  <div className="font-mono text-[11px] text-slate-500">{s.suspend_reason} · {fmtTs(s.suspended_at)}</div>
                </div>
                <button onClick={() => unsuspend(s.user_id)} data-testid={`unsuspend-${s.user_id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-4 py-2 text-xs font-semibold text-slate-950 hover:bg-blue-400 transition-colors duration-150">
                  <RotateCcw className="w-3.5 h-3.5" /> Reinstate
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-slate-200 mb-3">Top users today</h3>
      <div className="overflow-x-auto rounded-xl border border-slate-800 mb-8">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80">
            <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Reads</th><th className="px-4 py-3">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {data?.top_today?.map((t) => (
              <tr key={t.user_id} className="border-t border-slate-800/60">
                <td className="px-4 py-3 text-slate-200">{t.email}</td>
                <td className="px-4 py-3 font-mono text-slate-100">{t.count.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{fmtTs(t.last)}</td>
              </tr>
            ))}
            {(!data?.top_today || data.top_today.length === 0) && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500">No activity recorded yet today.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 className="text-lg font-semibold text-slate-200 mb-3">Recent access events</h3>
      <div className="rounded-xl border border-slate-800 divide-y divide-slate-800/60">
        {data?.recent?.map((e, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
            <span className="text-slate-300">{e.email}</span>
            <span className="font-mono text-[11px] text-slate-500">{e.method} {e.path}</span>
            <span className="font-mono text-[11px] text-slate-500">{e.ip}</span>
            <span className="font-mono text-[11px] text-slate-500">{fmtTs(e.ts)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
